import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { StagedDestinationWarmer } from '@ui-rocco/pages/directory-browse/StagedDestinationWarmer';
import { useAtomValue, useSetAtom } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { Button as RoccoButton } from '@rocco/components/button';
import { Switch } from '@rocco/components/switch';
import {
    Filters,
    FiltersProvider,
    type FilterSortOption,
} from '@rocco/ui/filter-sort/components/Filters';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { BackButton } from '../products-page/components/BackButton';
import { useDragScroll } from '../category-page/hooks/useDragScroll';
import { useScrollFade } from '../category-page/hooks/useScrollFade';
import { AppliedFiltersRow } from '@rocco/ui/filter-sort/components/AppliedFiltersRow';
import { SortDropdown } from '@rocco/ui/filter-sort/components/SortDropdown';
import type { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import {
    FiltersModal,
    getActiveFiltersCount,
} from '@rocco/ui/filter-sort/components/filters-modal/FiltersModal';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { CategoryPills } from '~/ui-rocco/pages/category-page/components/CategoryPills';
import {
    BLEED_BREAKOUT_RIGHT,
    BLEED_GUTTER_X,
} from '~/ui-rocco/pages/detail-shared';
import { useDirectoryBrowseTheme } from './directory-browse-theme';
import { useDirectoryFilterDrawer } from './directory-filter-drawer';
import { DirectoryDisplayDropdown } from './DirectoryDisplayDropdown';
import type { DirectoryGridColumns } from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';
import { useSortControlsEnabled } from '@modules/search/config/sort-controls';

/** How the bar presents itself; see `variant` on the props. */
export type DirectoryPillBarVariant = 'category' | 'imageBrowse';

export interface DirectoryPillBarBreadcrumb {
    href: string;
    label: string;
}

/**
 * The two browse modes a directory can list, e.g. Products and Brands.
 * Rendered on desktop as a labelled switch beside the Display control (see
 * {@link BrowseModeSwitch}) and inside the mobile refine sheet as pills. Used
 * by the revamped browse page in place of {@link DirectoryPillBarToggle}.
 */
export interface DirectoryPillBarEntitySwitch {
    /**
     * The browse modes in a fixed order. Order must not depend on which one is
     * active: the refine sheet renders them as pills, and reordering on switch
     * makes them jump under the user's finger.
     *
     * The desktop switch takes the first as its off position and the second as
     * its on position, so only the first two are shown there; the mobile sheet
     * shows all of them.
     */
    modes: {
        label: string;
        isActive: boolean;
        onSelect: () => void;
    }[];
}

/**
 * Optional entity toggle rendered on the right of the bar (e.g. products'
 * Products/Brands switch). Entities without a second mode (professionals,
 * projects) omit this and the toggle UI is not rendered.
 */
export interface DirectoryPillBarToggle {
    leftLabel: string;
    rightLabel: string;
    /** Whether the right-hand mode is active (e.g. the Brands view). */
    isRightActive: boolean;
    onToggle: () => void;
    localRightActive: boolean;
    disableHighlightTransition: boolean;
    setLocalRightActive: (value: boolean) => void;
    setDisableHighlightTransition: (value: boolean) => void;
    /** When the right mode is active, show these category pills instead of filter pills. */
    rightCategories?: { id: number; title: string; link: string }[];
}

export interface DirectoryPillBarProps {
    breadcrumbs: DirectoryPillBarBreadcrumb[];
    searchFilters: ReturnType<typeof useSearchFilters>;
    sortOptions: FilterSortOption[];
    /** Optional entity toggle. The legacy products bar passes it; the revamp uses entitySwitch. */
    toggle?: DirectoryPillBarToggle;
    /** Optional browse-mode switch, shown left of the Display control. */
    entitySwitch?: DirectoryPillBarEntitySwitch;
    /**
     * Overrides the noun in the refine sheet's apply CTA. Needed where the page
     * can switch what it lists, so the CTA does not keep saying "Products"
     * while counting brands.
     */
    resultName?: string;
    /**
     * Heading shown above the grid (the active category). Defaults to the
     * deepest breadcrumb, or "All Products" at the directory root.
     */
    sectionTitle?: string;
    /**
     * Child-category pills shown as the primary filter row (the 5.0 design uses
     * category pills, not facet dropdowns). Should include a leading "All …"
     * entry linking to the directory root. When omitted the bar falls back to
     * the facet filter dropdowns.
     */
    categoryPills?: { id: number; title: string; link: string }[];
    /**
     * Marks the selected pill on bars whose selection lives in a query param
     * rather than the path (the Spaces / Styles views). Omit on category bars,
     * where NavLink derives the active pill from the pathname.
     */
    activePillId?: number;
    /**
     * Bar presentation. `category` (default) is the labelled "Show Filters"
     * pill plus the parent back pill. `imageBrowse` is the Spaces / Styles bar:
     * an icon-only filter circle and no back pill, since the tab nav above
     * already leads back to /projects.
     */
    variant?: DirectoryPillBarVariant;
    /** Directory root path — used to label the back button (default /products). */
    rootPath?: string;
    /** Directory root label for the back button (default "All Products"). */
    rootLabel?: string;
    /**
     * Prompt in the in-listing search field. Defaults to the directory's own
     * noun, taken from `rootLabel`, so the projects bar does not invite a
     * product search; pass this only to override that wording.
     */
    searchPlaceholder?: string;
    /**
     * Whether the bar carries the in-listing keyword field. Directories whose
     * listing has no keyword search behind it (the editorial articles listing
     * reads from the CMS category, not the search service) turn it off rather
     * than offer a field that would silently do nothing.
     */
    showSearch?: boolean;
    /**
     * The URL the sheet's staged filters would commit to (from the settled
     * aggregate). While the sheet is open, its route data is prefetched so
     * the Apply navigation serves from the browser cache.
     */
    prefetchUrl?: string | null;
    /**
     * Current desktop column count for the "Display: {n}" dropdown beside
     * Sort. Provide together with onDisplayColumnsChange; omitted on pages
     * without the control (the legacy products browse).
     */
    displayColumns?: DirectoryGridColumns;
    /** Applies a new Display column count. */
    onDisplayColumnsChange?: (columns: DirectoryGridColumns) => void;
}

/**
 * Sliders glyph — two rails, each with a handle. Reads as "adjust these
 * settings", where the old descending-lines mark read as a generic list.
 */
const FilterPillIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="size-[1.15rem] shrink-0"
    >
        <path
            d="M3 8.5h9M18.5 8.5H21M3 15.5h3.5M13 15.5h8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
        />
        <circle
            cx="15.25"
            cy="8.5"
            r="2.6"
            stroke="currentColor"
            strokeWidth="1.7"
        />
        <circle
            cx="9.75"
            cy="15.5"
            r="2.6"
            stroke="currentColor"
            strokeWidth="1.7"
        />
    </svg>
);

const SearchGlyph = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="size-[1.05rem] shrink-0"
    >
        <circle
            cx="11"
            cy="11"
            r="6.5"
            stroke="currentColor"
            strokeWidth="1.6"
        />
        <path
            d="M15.8 15.8L20 20"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
    </svg>
);

const ClearGlyph = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="size-[0.9rem] shrink-0"
    >
        <path
            d="M6 6L18 18M18 6L6 18"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
        />
    </svg>
);

/**
 * Keyword search over the current listing. The search service already accepts a
 * term (`filterStates.states.search.query`, fed from the `search` URL param),
 * so this only has to drive the URL — the loader does the rest, and the result
 * is shareable and survives a refresh.
 *
 * Committing is explicit (Enter or clearing), not per-keystroke: each commit is
 * a navigation and a fresh query, so typing live would fire one per character.
 */
const DirectorySearchField = ({
    isDark,
    placeholder,
}: {
    isDark: boolean;
    placeholder: string;
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const belowMd = useIsBelowMd();
    const committed = new URLSearchParams(location.search).get('search') ?? '';
    const [value, setValue] = useState(committed);
    const inputRef = useRef<HTMLInputElement>(null);
    // A committed term is applied state, so the field takes the inactive pill's
    // flat fill rather than sitting there looking like an empty input.
    const isCommitted = committed.length > 0;

    // Re-seed when the committed term changes underneath us — Back/Forward, or
    // a category hop that keeps the term.
    useEffect(() => {
        setValue(committed);
    }, [committed]);

    const commit = (next: string) => {
        const params = new URLSearchParams(location.search);
        const trimmed = next.trim();
        if (trimmed) {
            params.set('search', trimmed);
        } else {
            params.delete('search');
        }
        // A new term starts a new result set — holding the old page number
        // would drop the reader into the middle of it.
        params.delete('page');
        const query = params.toString();
        // Searching is refining, so the reader holds their place — the same
        // rule the facets and the sort navigate under. The grid pulls them
        // back up on its own if the narrowed set no longer reaches the screen.
        // Below md the sheet's own commits scroll, so this follows: there the
        // refreshed results have to come into view to be seen at all.
        navigate(`${location.pathname}${query ? `?${query}` : ''}`, {
            preventScrollReset: !belowMd,
        });
    };

    // Every other query param, so a submit that happens before hydration is a
    // real GET to the same listing rather than one that drops the reader's
    // filters. `page` is left out for the same reason `commit` deletes it.
    const carriedParams = Array.from(
        new URLSearchParams(location.search).entries(),
    ).filter(([key]) => key !== 'search' && key !== 'page');

    return (
        <form
            role="search"
            method="get"
            action={location.pathname}
            onSubmit={(event) => {
                event.preventDefault();
                // Committing ends the editing session. A caret still blinking
                // in a field whose term has already been applied reads as
                // "not submitted yet".
                inputRef.current?.blur();
                commit(value);
            }}
            className={cn(
                // Wide enough for the full placeholder to sit un-clipped —
                // a truncated prompt is worse than a shorter one.
                'flex h-10.5 w-[17.5rem] shrink-0 items-center gap-2 rounded-full border px-4 transition-colors',
                // Desktop-only as a control: mobile refines through the sheet.
                // A committed term is the exception — `search` survives in the
                // URL, and Clear all does not touch it, so a shared or
                // bookmarked link would otherwise filter the listing on mobile
                // with nothing on screen saying so and no way to undo it.
                isCommitted ? 'max-md:w-[10.5rem]' : 'max-md:hidden',
                isDark ? 'text-offwhite' : 'text-[#323232]',
                isCommitted
                    ? // Applied: flat fill, no outline — the inactive pill's
                      // treatment, since that is what it now is.
                      isDark
                        ? 'border-transparent bg-offwhite/10'
                        : 'border-transparent bg-[#F2F2F2]'
                    : isDark
                      ? 'border-offwhite/25'
                      : 'border-[#141414]/20',
                // Focusing to edit returns it to an input: the outline comes
                // back and the fill drops away, so the caret has a frame.
                isDark
                    ? 'focus-within:border-offwhite/60 focus-within:bg-transparent'
                    : 'focus-within:border-[#141414]/50 focus-within:bg-transparent',
            )}
        >
            <span className={isDark ? 'text-offwhite/60' : 'text-[#767676]'}>
                <SearchGlyph />
            </span>
            {carriedParams.map(([key, carried], index) => (
                <input
                    key={`${key}-${index}`}
                    type="hidden"
                    name={key}
                    value={carried}
                />
            ))}
            <input
                ref={inputRef}
                // Not type="search": WebKit draws its own clear affordance for
                // that type, which would sit beside ours.
                type="text"
                name="search"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder={placeholder}
                aria-label="Search this listing"
                className={cn(
                    'min-w-0 flex-1 border-none bg-transparent text-[1rem] tracking-[-0.02rem] outline-none',
                    isDark
                        ? 'text-offwhite placeholder:text-offwhite/50'
                        : 'text-[#323232] placeholder:text-[#767676]',
                )}
            />
            {value || committed ? (
                <button
                    type="button"
                    onClick={() => {
                        setValue('');
                        commit('');
                    }}
                    aria-label="Clear search"
                    className={cn(
                        'flex shrink-0 cursor-pointer items-center justify-center bg-transparent p-0 transition-colors',
                        isDark
                            ? 'text-offwhite/60 hover:text-offwhite'
                            : 'text-[#767676] hover:text-[#323232]',
                    )}
                >
                    <ClearGlyph />
                </button>
            ) : null}
        </form>
    );
};

/**
 * Separator between crumbs in the section heading. Sized in `em` so it tracks
 * the heading's type, and muted so the crumb names carry the row.
 */
const BreadcrumbChevron = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="mx-2 inline-block size-[0.7em] align-middle opacity-40"
    >
        <path
            d="M9 5L16 12L9 19"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/** Shared visual style for both the desktop and mobile "Show Filters" pill.
 * `max-md:min-h-[44px]` guarantees a touch-friendly hit area below md even
 * though the visual pill height (h-10.5) sits a little under 44px at small
 * viewports; the row's `overflow-visible` already tolerates the pill
 * overflowing its row slightly. */
const filterPillClassName = (isDark: boolean) =>
    cn(
        'flex h-10.5 shrink-0 items-center gap-2 rounded-full px-[1.25rem] text-[1rem] tracking-[-0.05rem] transition-colors max-md:min-h-[44px]',
        isDark
            ? 'bg-[#d5d5d5] text-[#141414] hover:bg-[#c8c8c8] active:bg-[#bcbcbc]'
            : 'bg-[#323232] text-white hover:bg-[#484848] active:bg-[#515151]',
    );

/**
 * Applied-facet count badge for the "Show Filters" pill — once the sidebar
 * (desktop) or bottom sheet (mobile) is closed, this is the only visible
 * indicator that filters are active.
 */
const FilterPillCount = ({
    count,
    isDark,
}: {
    count: number;
    isDark: boolean;
}) => {
    if (count <= 0) return null;
    return (
        <span
            className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-medium',
                isDark
                    ? 'bg-[#141414] text-offwhite'
                    : 'bg-white text-[#323232]',
            )}
        >
            {count}
        </span>
    );
};

/**
 * "Show Filters" / "Hide Filters" pill (md and above) — toggles the left
 * facet sidebar. Renders nothing when the page has no sidebar.
 */
const FilterToggleButtonDesktop = ({
    isDark,
    activeFiltersCount,
    compact = false,
}: {
    isDark: boolean;
    activeFiltersCount: number;
    compact?: boolean;
}) => {
    const { open, toggle, hasSidebar } = useDirectoryFilterDrawer();
    if (!hasSidebar) return null;
    if (compact) {
        // Figma 5762:114460 — a 42px icon-only circle, no label.
        return (
            <button
                type="button"
                onClick={toggle}
                aria-pressed={open}
                aria-label={open ? 'Hide filters' : 'Show filters'}
                className={cn(
                    'relative flex size-10.5 shrink-0 items-center justify-center rounded-full transition-colors max-md:hidden',
                    isDark
                        ? 'bg-[#d5d5d5] text-[#181818] hover:bg-[#c8c8c8]'
                        : 'bg-[#181818] text-white hover:bg-[#2a2a2a]',
                )}
            >
                <FilterPillIcon />
                {activeFiltersCount > 0 ? (
                    <span className="absolute -top-0.5 -right-0.5">
                        <FilterPillCount
                            count={activeFiltersCount}
                            isDark={isDark}
                        />
                    </span>
                ) : null}
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={toggle}
            aria-pressed={open}
            // Icon-only, so the label moves to aria-label — without it the
            // control is unnamed for anyone not seeing the glyph.
            aria-label={open ? 'Hide Filters' : 'Show Filters'}
            // The breakpoint gate must be modifier-scoped (max-md:hidden), not a
            // bare `hidden`: the base pill style starts with `flex`, and
            // tailwind-merge treats an unscoped hidden/flex pair as one display
            // conflict and keeps only the later class — silently un-hiding this
            // pill on mobile.
            className={cn(
                filterPillClassName(isDark),
                'relative size-10.5 justify-center px-0 max-md:hidden',
            )}
        >
            <FilterPillIcon />
            {activeFiltersCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5">
                    <FilterPillCount
                        count={activeFiltersCount}
                        isDark={isDark}
                    />
                </span>
            ) : null}
        </button>
    );
};

/**
 * "Refine" pill (below md) — the persistent sidebar has nowhere to live on
 * mobile (CollapsiblePageLayout hides it there), so this opens the shared
 * FiltersModal bottom sheet (which also carries Sort By on mobile) instead. It
 * is styled as a peer of the child-category pills rather than the heavier
 * desktop toggle, so the row reads as a single set of pills.
 */
const FilterToggleButtonMobile = ({
    isDark,
    activeFiltersCount,
}: {
    isDark: boolean;
    activeFiltersCount: number;
}) => {
    const { hasSidebar } = useDirectoryFilterDrawer();
    const isSheetOpen = useAtomValue(uiStateAtomFiltersModalIsOpened);
    const setSheetOpen = useSetAtom(uiStateAtomFiltersModalIsOpened);
    if (!hasSidebar) return null;
    return (
        <button
            type="button"
            onClick={() => setSheetOpen((prev) => !prev)}
            aria-pressed={isSheetOpen}
            aria-label="Refine"
            className={cn(
                // Figma 2354:63263 — icon-only charcoal circle (inverts on the
                // dark editorial theme). The count badge overlays the corner
                // so applied-filter state stays visible without a label.
                'relative flex size-11 shrink-0 items-center justify-center rounded-full md:hidden',
                isDark
                    ? 'bg-offwhite text-[#141414]'
                    : 'bg-[#323232] text-white',
            )}
        >
            <FilterPillIcon />
            {activeFiltersCount > 0 ? (
                <span className="absolute -top-0.5 -right-0.5">
                    <FilterPillCount
                        count={activeFiltersCount}
                        isDark={isDark}
                    />
                </span>
            ) : null}
        </button>
    );
};

/**
 * Browse-mode switch on the right of row 2 (e.g. Show Products / Show
 * Brands). Both modes are named either side of the track, so which one is
 * active reads at a glance — and either label is clickable, not just the
 * switch. The track keeps its colour in both states because this expresses a
 * position between two equals, not an on/off.
 */
const BrowseModeSwitch = ({
    modes,
    isDark,
}: {
    modes: DirectoryPillBarEntitySwitch['modes'];
    isDark: boolean;
}) => {
    const [first, second] = modes;
    if (!first || !second) {
        return null;
    }

    const labelClass = (isActive: boolean) =>
        cn(
            'cursor-pointer bg-transparent p-0 text-[1rem] leading-[1.5rem] font-normal whitespace-nowrap transition-colors',
            isActive
                ? isDark
                    ? 'text-offwhite'
                    : 'text-[#323232]'
                : isDark
                  ? 'text-offwhite/50'
                  : 'text-[#6b6b6f]',
        );

    return (
        <div className="flex shrink-0 items-center gap-3">
            <button
                type="button"
                onClick={first.onSelect}
                className={labelClass(first.isActive)}
            >
                {`Show ${first.label}`}
            </button>
            <Switch
                checked={second.isActive}
                onCheckedChange={(checked) =>
                    (checked ? second : first).onSelect()
                }
                // Names the control, not its on position: the button directly
                // to the right already says "Show {second}", and two adjacent
                // controls with the same accessible name are indistinguishable
                // in a list of form controls.
                aria-label="Browse mode"
                // Light: a step lighter than the page ink. The switch is a
                // quiet control sitting between two labels, so the full
                // #323232 read heavier than the row around it — but the track
                // still has to carry the white thumb, which has no shadow.
                className={cn(
                    isDark
                        ? 'data-[state=checked]:bg-offwhite data-[state=unchecked]:bg-offwhite'
                        : 'data-[state=checked]:bg-[#525252] data-[state=unchecked]:bg-[#525252]',
                )}
                // The rocco thumb ships without a shadow; a soft one lifts it
                // off the track so the knob reads as a movable part.
                thumbClassName={cn(
                    'shadow-[0_1px_3px_rgba(0,0,0,0.28)]',
                    isDark ? 'bg-[#141414]' : 'bg-white',
                )}
            />
            <button
                type="button"
                onClick={second.onSelect}
                className={labelClass(second.isActive)}
            >
                {`Show ${second.label}`}
            </button>
        </div>
    );
};

/** Matches Tailwind's `md` breakpoint, for the two things in this bar that
 * genuinely behave differently by viewport: the "All Filters" modal (bottom
 * sheet + staged Apply on mobile vs. left panel + immediate apply on desktop)
 * and whether committing a search holds the reader's scroll position.
 * SSR-safe: starts false and resolves on mount. */
const BELOW_MD_QUERY = '(max-width: 767.98px)';

const useIsBelowMd = (): boolean => {
    const [belowMd, setBelowMd] = useState(false);
    useEffect(() => {
        const query = window.matchMedia(BELOW_MD_QUERY);
        setBelowMd(query.matches);
        const onChange = () => setBelowMd(query.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);
    return belowMd;
};

/**
 * Shared desktop filter/pill bar for the directory browse pages (products,
 * professionals, projects). Back button + scrollable filter pills + Sort, with
 * an optional entity toggle. Generalised from the products-only filter bar so
 * all three entities render the same bar off their own filter config.
 */
export const DirectoryPillBar = ({
    breadcrumbs,
    searchFilters,
    sortOptions,
    toggle,
    entitySwitch,
    resultName,
    sectionTitle,
    categoryPills,
    activePillId,
    variant = 'category',
    rootPath = '/products',
    rootLabel = 'All Products',
    searchPlaceholder,
    showSearch = true,
    prefetchUrl = null,
    displayColumns,
    onDisplayColumnsChange,
}: DirectoryPillBarProps) => {
    const { scrollRef } = useDragScroll();
    const location = useLocation();
    const isSheetOpen = useAtomValue(uiStateAtomFiltersModalIsOpened);
    const setSheetOpen = useSetAtom(uiStateAtomFiltersModalIsOpened);
    const isDark = useDirectoryBrowseTheme() === 'dark';
    // "All Products" -> "Search Products...". Every caller already names its
    // root, so the field describes the listing it filters without each route
    // having to restate it.
    const searchPrompt =
        searchPlaceholder ?? `Search ${rootLabel.replace(/^All /, '')}...`;
    // Which of the two bars this is: the browse redesign passes a Display
    // control, the legacy products browse does not and shows Sort instead.
    const hasDisplayControl =
        displayColumns !== undefined && onDisplayColumnsChange !== undefined;
    // Revamp browse (`category_pages_revamp_2026`) uses Display in this slot.
    // Current-site products omit Display, so Sort by comes back with the flag.
    const showSort = useSortControlsEnabled() && !hasDisplayControl;
    // The page ground behind the pill scroller, for its edge fades. Inline
    // rather than a class because both fades are gradient stops.
    const fadeGround = isDark ? '#141414' : 'white';
    const isBelowMd = useIsBelowMd();
    const { hasSidebar } = useDirectoryFilterDrawer();
    // Below md the leading cluster carries the refine circle only where there
    // is a sidebar, and the search field only once a term is committed. With
    // neither there is nothing in front of the pills, so the divider that
    // separates them has to stand down too rather than rule off empty space.
    const hasCommittedSearch =
        showSearch &&
        (new URLSearchParams(location.search).get('search') ?? '') !== '';
    // Applied-facet count for the "Show Filters" pill badge, and to gate
    // mounting the mobile FiltersModal bottom sheet below.
    const activeFiltersCount = getActiveFiltersCount(
        searchFilters.filterStates,
    );

    // Breadcrumb trail for the heading, e.g. "All Products – (Bathroom)". Each
    // crumb keeps its own short category name; only the root is relabelled, to
    // the directory's own label, so it reads as the pill that leads the row
    // above. The deepest crumb is the active one and carries the brackets.
    // With no breadcrumbs at all (the directory root) the trail is that single
    // root crumb, which `sectionTitle` may name instead.
    const trail = React.useMemo(() => {
        if (breadcrumbs.length === 0) {
            return [{ href: rootPath, label: sectionTitle ?? rootLabel }];
        }
        return breadcrumbs.map((crumb, index) => ({
            href: crumb.href,
            label: index === 0 ? rootLabel : crumb.label,
        }));
    }, [breadcrumbs, rootPath, rootLabel, sectionTitle]);

    // Track scroll position for dynamic fade masks / grab cursor
    const { showLeftFade, showRightFade, isOverflowing } = useScrollFade(
        scrollRef as React.RefObject<HTMLDivElement | null>,
    );

    // Extract parent category info from breadcrumbs (second-to-last is the parent)
    const parentBreadcrumb =
        breadcrumbs?.length >= 2 ? breadcrumbs[breadcrumbs.length - 2] : null;
    const parentCategoryName = parentBreadcrumb?.label;
    const parentCategoryLink = parentBreadcrumb?.href;
    const shouldShowBackButton = !!parentCategoryLink && !!parentCategoryName;

    const showRightCategoryPills =
        !!toggle?.isRightActive &&
        !!toggle.rightCategories &&
        toggle.rightCategories.length > 0;

    return (
        <>
            {/* Row 1: Filter bar - fixed height prevents layout shift, overflow-visible for badges.
                `max-md:min-h-[44px]` floors the row below md so its pill children
                (which need a ≥44px tap target there) aren't squeezed into the
                slimmer 42px desktop visual — overflow-visible already tolerates
                the row's content overflowing that box on the 2px difference. */}
            {/* Mobile vertical rhythm lives on the sticky pill-bar section
                (DirectoryBrowsePage) so the bar's background wraps it when
                stuck to the header.

                md:overflow-x-clip — the pill scrollers below cancel this row's
                gutter with a 100vw-based negative margin, which stays half a
                classic (non-overlay) scrollbar wider than the row's %-based
                padding past 1920. Clipping the x axis here keeps that overhang
                from becoming a horizontal page scroll; y stays visible, so the
                44px mobile pills and the count badges still overflow the row.
                Clipping the row does not affect its own stickiness — only an
                ancestor's overflow would. */}
            <div
                className={cn(
                    'flex h-10.5 items-center gap-3 overflow-visible px-4 max-md:min-h-[44px] md:overflow-x-clip',
                    // Desktop: pins to the viewport top and the grid scrolls
                    // behind it, frosted. The site header is in-flow here and
                    // scrolls away, so the offset is 0 rather than a header
                    // height. The translucent fill is what the blur samples —
                    // over an opaque background it would have nothing to show.
                    // box-content so the band's padding sits outside the row's
                    // own 42px, leaving the controls centred in it.
                    'md:sticky md:top-0 md:z-[115] md:box-content md:py-[1.44rem] md:backdrop-blur-xl',
                    isDark ? 'md:bg-[#141414]/70' : 'md:bg-white/70',
                    // md+ the row's gutters track the centred 1920 container
                    // (48px until the cap binds) while the pill scrollers
                    // below break back out to the viewport edge.
                    BLEED_GUTTER_X,
                )}
            >
                {/* Back Button - only show when not at root. In the category-pill
                    layout it moves into the category group (after Show Filters);
                    here it leads the other layouts. */}
                {!categoryPills &&
                    shouldShowBackButton &&
                    parentCategoryName && (
                        <BackButton
                            parentCategoryName={parentCategoryName}
                            parentCategoryLink={parentCategoryLink}
                        />
                    )}

                {/* Filter Pills or Category Pills - scrollable with fade masks */}
                {showRightCategoryPills && toggle?.rightCategories ? (
                    // Full-bleed to the row's right edge, matching the
                    // child-category pills branch below. `max-md:[&_a]:min-h`
                    // floors each pill link's tap target at 44px below md —
                    // CategoryPills is shared elsewhere so this can't be set
                    // on its own h-10.5 (42px) pills directly.
                    <div
                        className={cn(
                            '-mr-4 flex min-w-0 flex-1 max-md:[&_a]:min-h-[44px]',
                            BLEED_BREAKOUT_RIGHT,
                        )}
                    >
                        <CategoryPills categories={toggle.rightCategories} />
                    </div>
                ) : categoryPills ? (
                    // 5.0 design: a "Show Filters" pill (opens the facet modal) |
                    // separator | back button | the child-category pills. The
                    // facet dropdowns move into the modal, so this row is category
                    // navigation. The pills scroll horizontally (hidden scrollbar)
                    // rather than pushing the page wide, so everything else is
                    // shrink-0 and the pills take the remaining width (min-w-0).
                    <>
                        <>
                            <div className="flex shrink-0 items-center gap-3">
                                {/* Pages without a sidebar (e.g. the Brands
                                    view) carry no facets, so only the filter
                                    toggles stand down. Searching the listing is
                                    a separate act from refining it by facet —
                                    the field stays. */}
                                {hasSidebar && (
                                    <>
                                        <FilterToggleButtonDesktop
                                            isDark={isDark}
                                            activeFiltersCount={
                                                activeFiltersCount
                                            }
                                            compact={variant === 'imageBrowse'}
                                        />
                                        <FilterToggleButtonMobile
                                            isDark={isDark}
                                            activeFiltersCount={
                                                activeFiltersCount
                                            }
                                        />
                                    </>
                                )}
                                {showSearch && (
                                    <DirectorySearchField
                                        isDark={isDark}
                                        placeholder={searchPrompt}
                                    />
                                )}
                            </div>
                            {/* Mobile divider between the refine circle and the
                                scrolling pill set (desktop has its own further in). */}
                            {(hasSidebar || hasCommittedSearch) && (
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        'h-[2.1rem] w-px shrink-0 md:hidden',
                                        isDark
                                            ? 'bg-offwhite/20'
                                            : 'bg-[#232323]/20',
                                    )}
                                />
                            )}
                        </>
                        {hasSidebar &&
                            searchFilters.config.modalFilters.length > 0 && (
                                // Below md there is no persistent sidebar to toggle
                                // (CollapsiblePageLayout hides it there) — mount the
                                // shared FiltersModal bottom sheet here, driven by
                                // the mobile pill above via the shared open-state
                                // atom. Its own internal trigger button is unused
                                // (our pill drives `open`), so it's kept out of the
                                // visual layout; the Sheet content itself still
                                // portals and displays normally when opened.
                                // `showSorting` surfaces sort inside the sheet
                                // (mobile displayMode only), since row 2's desktop
                                // Sort dropdown is hidden below md. The refine
                                // presentation stages sort with the other
                                // controls and commits everything through the
                                // sheet's CTA; the staged category tree drills
                                // through the vertical's category hierarchy
                                // without leaving the sheet.
                                <FiltersProvider
                                    searchFilters={searchFilters}
                                    sortOptions={sortOptions}
                                    displayMode={DisplayMode.Mobile}
                                >
                                    <div className="hidden">
                                        <FiltersModal
                                            showSorting={showSort}
                                            presentation="refine"
                                            categoryTree={{ rootLabel }}
                                            // Mobile counterpart of the
                                            // "Prefer to browse by …" prompt,
                                            // which is desktop-only.
                                            browseMode={
                                                entitySwitch && {
                                                    // Switching navigates, so
                                                    // dismiss the sheet rather
                                                    // than leaving it covering
                                                    // the new mode's grid.
                                                    options:
                                                        entitySwitch.modes.map(
                                                            (mode) => ({
                                                                ...mode,
                                                                onSelect:
                                                                    () => {
                                                                        setSheetOpen(
                                                                            false,
                                                                        );
                                                                        mode.onSelect();
                                                                    },
                                                            }),
                                                        ),
                                                }
                                            }
                                            resultName={resultName}
                                        />
                                    </div>
                                </FiltersProvider>
                            )}
                        {/* Warm the staged destination while the sheet is
                            open: by the time the user taps Apply, the route
                            data is already in the browser's HTTP cache
                            (entry.server gives prefetch-purposed data
                            requests a short max-age). PrefetchPageLinks
                            computes the single-fetch URL; the wrapper span
                            lets StagedDestinationWarmer find the rendered
                            link and back it with a fetch — Safari has no
                            link-prefetch support at all, and memory-only
                            caches (incognito) evict link entries under
                            image churn, so the link alone is not reliable. */}
                        {isSheetOpen &&
                            prefetchUrl &&
                            prefetchUrl !==
                                location.pathname + location.search && (
                                <StagedDestinationWarmer page={prefetchUrl} />
                            )}
                        {/* Separator + parent back pill are desktop chrome. Below
                            md they crowd the row and the Refine pill instead sits
                            directly among the category pills, so hide the whole
                            group there — mobile users navigate back via the pills
                            or the browser. Base `flex` + scoped `max-md:hidden`
                            keep desktop spacing byte-for-byte (the row's gap-3 now
                            lands on this wrapper) while collapsing it below md. */}
                        <div className="flex shrink-0 items-center gap-3 max-md:hidden">
                            <span
                                aria-hidden="true"
                                className={cn(
                                    'h-[2.1rem] w-px shrink-0',
                                    isDark
                                        ? 'bg-offwhite/20'
                                        : 'bg-[#232323]/20',
                                )}
                            />
                            {variant === 'category' &&
                                shouldShowBackButton &&
                                parentCategoryName && (
                                    <BackButton
                                        // A level-1 category's parent is the root listing:
                                        // label it "All {Entity}" rather than the root
                                        // breadcrumb's bare name.
                                        parentCategoryName={
                                            parentCategoryLink === rootPath
                                                ? rootLabel
                                                : parentCategoryName
                                        }
                                        parentCategoryLink={parentCategoryLink}
                                        variant={isDark ? 'light' : 'dark'}
                                    />
                                )}
                        </div>
                        {/* Full-bleed: cancel the row's right padding so the pills
                            scroll all the way to the container edge on overflow.
                            `max-md:[&_a]:min-h` floors each pill link's tap
                            target at 44px below md — CategoryPills is shared
                            elsewhere so this can't be set on its own h-10.5
                            (42px) pills directly. */}
                        <div
                            className={cn(
                                '-mr-4 -ml-3 flex min-w-0 flex-1 max-md:[&_a]:min-h-[44px]',
                                BLEED_BREAKOUT_RIGHT,
                            )}
                        >
                            <CategoryPills
                                categories={categoryPills}
                                activeId={activePillId}
                                theme={isDark ? 'dark' : 'light'}
                                // Mobile keeps the parent back pill but lets it
                                // scroll away with the set — desktop pins it
                                // outside the scroller instead.
                                leading={
                                    variant === 'category' &&
                                    shouldShowBackButton &&
                                    parentCategoryName ? (
                                        <BackButton
                                            parentCategoryName={
                                                parentCategoryLink === rootPath
                                                    ? rootLabel
                                                    : parentCategoryName
                                            }
                                            parentCategoryLink={
                                                parentCategoryLink
                                            }
                                            variant={isDark ? 'light' : 'dark'}
                                        />
                                    ) : undefined
                                }
                            />
                        </div>
                    </>
                ) : (
                    // Full-bleed on the right edge too, matching the
                    // category-pill branches above (and the category-carousel
                    // convention: left padding stays, right padding is
                    // cancelled so the row scrolls flush to the edge).
                    <div
                        className={cn(
                            '-mr-4 min-w-0 flex-1 overflow-visible',
                            BLEED_BREAKOUT_RIGHT,
                        )}
                    >
                        <div className="relative overflow-visible">
                            {/* Left fade overlay - only visible when scrolled */}
                            {showLeftFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
                                    style={{
                                        // Fades to the page ground, not to a
                                        // fixed white — on the dark editorial
                                        // theme a white fade would read as a
                                        // glow over the near-black page.
                                        background: `linear-gradient(to right, ${fadeGround}, transparent)`,
                                    }}
                                />
                            )}
                            {/* Right fade overlay - only visible when more content
                                available. Hidden below md (`max-md:hidden`) so the
                                pill scroller matches the mobile carousels' full-bleed
                                hard right edge; kept at md+. */}
                            {showRightFade && (
                                <div
                                    className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 max-md:hidden"
                                    style={{
                                        background: `linear-gradient(to left, ${fadeGround}, transparent)`,
                                    }}
                                />
                            )}
                            {/* Scroll container - badge space (-my-4 py-4) is INSIDE to prevent clipping.
                                Only show the grab cursor when pills actually overflow; otherwise the
                                flex-1 strip makes the empty space look draggable. */}
                            <div
                                ref={
                                    scrollRef as React.RefObject<HTMLDivElement | null>
                                }
                                className={cn(
                                    '-my-4 scrollbar-hide flex items-center gap-3 overflow-x-auto py-4',
                                    isOverflowing &&
                                        'md:cursor-grab md:active:cursor-grabbing',
                                )}
                            >
                                {searchFilters && (
                                    <Filters
                                        searchFilters={searchFilters}
                                        sortOptions={sortOptions}
                                        // Pinned to Desktop so the static bar
                                        // filter pills below still render
                                        // below md — FiltersBar (which they
                                        // and barTrailingComponent live in)
                                        // only renders when this resolves to
                                        // Desktop, and the mobile pill bar is
                                        // deliberately the same horizontal
                                        // scroller, not a different layout.
                                        // The "All Filters" trigger below is
                                        // the one piece that genuinely
                                        // differs by viewport (bottom sheet +
                                        // staged Apply on mobile vs. left
                                        // panel + immediate apply on desktop),
                                        // so it gets the real, client-resolved
                                        // mode via its own provider instead of
                                        // inheriting this Desktop pin.
                                        displayMode={DisplayMode.Desktop}
                                        showSortBy={false}
                                        hideModalButton={true}
                                        showSelectedCountBadge={true}
                                        disableWrap={true}
                                        filterPillClassName="border-[#F2F2F2] bg-[#F2F2F2] text-[#323232] hover:border-gray-200 hover:bg-gray-200 active:border-gray-300 active:bg-gray-300 px-5 py-2.5 tracking-tighter max-md:min-h-[44px] [&>svg]:size-3.5 [&>svg]:ml-2.5"
                                        scrollableClassName="gap-3"
                                        filterButtonClassName="px-5 py-2.5 tracking-tighter [&>svg]:size-4 [&>svg]:ml-2.5"
                                        barTrailingComponent={
                                            toggle?.isRightActive ? undefined : (
                                                <FiltersProvider
                                                    searchFilters={
                                                        searchFilters
                                                    }
                                                    sortOptions={sortOptions}
                                                    displayMode={
                                                        isBelowMd
                                                            ? DisplayMode.Mobile
                                                            : DisplayMode.Desktop
                                                    }
                                                    filterButtonClassName="px-5 py-2.5 tracking-tighter [&>svg]:size-4 [&>svg]:ml-2.5"
                                                >
                                                    <FiltersModal
                                                        showSorting={false}
                                                    />
                                                </FiltersProvider>
                                            )
                                        }
                                        hideFacetFilters={true}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Entity toggle (optional) - e.g. Products/Brands */}
                {toggle && (
                    <div className="ml-auto flex shrink-0 items-center gap-3">
                        <div className="relative flex items-center gap-2 rounded-full bg-[#ECECEC] px-[0.45rem] py-[0.3rem]">
                            {/* Sliding highlight background */}
                            <div
                                aria-hidden
                                className={cn(
                                    'absolute top-[0.3rem] left-[0.45rem] h-[calc(100%-0.6rem)] rounded-full bg-white shadow-md duration-300',
                                    toggle.disableHighlightTransition
                                        ? ''
                                        : 'transition-transform',
                                    toggle.localRightActive
                                        ? 'translate-x-full'
                                        : 'translate-x-0',
                                )}
                                style={{
                                    width: 'calc(50% - 0.45rem)',
                                }}
                            />

                            <RoccoButton
                                weight="normal"
                                clickAnimation="scale"
                                onClick={() => {
                                    if (toggle.isRightActive) {
                                        toggle.setDisableHighlightTransition(
                                            true,
                                        );
                                        toggle.setLocalRightActive(false);
                                        requestAnimationFrame(() => {
                                            setTimeout(() => {
                                                toggle.onToggle();
                                                toggle.setDisableHighlightTransition(
                                                    false,
                                                );
                                            }, 50);
                                        });
                                    }
                                }}
                                className={cn(
                                    'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                    !toggle.isRightActive
                                        ? 'font-medium text-[#232323]'
                                        : 'font-normal text-[#232323] opacity-60',
                                )}
                            >
                                <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                    {toggle.leftLabel}
                                </span>
                            </RoccoButton>

                            <RoccoButton
                                weight="normal"
                                clickAnimation="scale"
                                onClick={() => {
                                    if (!toggle.isRightActive) {
                                        toggle.setDisableHighlightTransition(
                                            true,
                                        );
                                        toggle.setLocalRightActive(true);
                                        requestAnimationFrame(() => {
                                            setTimeout(() => {
                                                toggle.onToggle();
                                                toggle.setDisableHighlightTransition(
                                                    false,
                                                );
                                            }, 50);
                                        });
                                    }
                                }}
                                className={cn(
                                    'relative z-10 flex-1 rounded-full border-none bg-transparent px-8 py-2.5 hover:bg-transparent',
                                    toggle.isRightActive
                                        ? 'font-medium text-[#232323]'
                                        : 'font-normal text-[#232323] opacity-60',
                                )}
                            >
                                <span className="tracking-0 md:tracking-0 text-sm leading-[1.625rem] md:text-base">
                                    {toggle.rightLabel}
                                </span>
                            </RoccoButton>
                        </div>
                    </div>
                )}
            </div>

            {/* Row 2: Section title (active category) + Sort By.
                The visible breadcrumb row is replaced by the category heading
                per the 5.0 design; breadcrumb data still feeds the page's
                JSON-LD (SEO) elsewhere. Hidden below md (`max-md:hidden`): the
                heading is desktop-only and Sort By moves into the mobile filter
                sheet, so the whole row collapses to reclaim mobile real estate. */}
            {/* The rule sits centred in its own band: the gap up to the pills
                matches the gap down to the heading's text. Below the rule the
                heading is centred against the taller Display control, which
                adds ~6px on top of the row's own padding — hence 2rem here
                rather than the row's 1.25rem. */}
            {/* The gap to the pills above is mostly the sticky band's own
                bottom padding, which is symmetric and centres the controls in
                the frosted bar — so the trim comes out of this padding rather
                than that one, which would move the space above the pills too. */}
            <div
                className={cn('px-4 pt-[0.3rem] max-md:hidden', BLEED_GUTTER_X)}
            >
                <hr
                    className={cn(
                        // The colour is the colour: no element opacity, which
                        // would render it as something other than what it says.
                        'm-0 h-0 border-t',
                        isDark ? 'border-offwhite/20' : 'border-[#E9E9E9]',
                    )}
                />
                <div className="flex items-center justify-between gap-4 pt-5 pb-6">
                    {/* The browse-mode switch lives on the right with Display,
                        so the left of the row is just the active category. */}
                    <div className="flex min-w-0 items-baseline gap-4">
                        <h2
                            className={cn(
                                // Set to the same 1rem as the category pills
                                // above and the controls opposite, so the whole
                                // bar reads as one row. Truncated so a long
                                // trail can't collide with the controls on a
                                // narrow viewport — it never wraps to a second
                                // line.
                                // One weight up from the controls opposite, so
                                // the trail reads as the heading of the row
                                // without going full bold.
                                'm-0 min-w-0 truncate text-base leading-[1.5rem] font-medium tracking-[-0.02rem]',
                                isDark ? 'text-offwhite' : 'text-[#323232]',
                            )}
                        >
                            {trail.map((crumb, index) => {
                                // Brackets mark the active crumb, so it needs no
                                // link — it is the page you are on.
                                const isActive = index === trail.length - 1;
                                return (
                                    <React.Fragment key={crumb.href}>
                                        {index > 0 && <BreadcrumbChevron />}
                                        {isActive ? (
                                            <span aria-current="page">
                                                {`(${crumb.label})`}
                                            </span>
                                        ) : (
                                            <Link
                                                to={crumb.href}
                                                prefetch="intent"
                                                className="underline-offset-4 hover:underline"
                                            >
                                                {crumb.label}
                                            </Link>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </h2>
                    </div>
                    <div className="flex shrink-0 items-center gap-8">
                        {entitySwitch && (
                            <BrowseModeSwitch
                                modes={entitySwitch.modes}
                                isDark={isDark}
                            />
                        )}
                        {/* Display sits to the right of the browse-mode switch,
                        and is what tells the two bars apart: the legacy
                        products browse renders this component without it, and
                        keeps Sort By as its only control here. The browse
                        redesign replaces Sort with Display, so the two are
                        mutually exclusive.

                        The wrapper restyles only the trigger label + chevron
                        (options are portaled), matching the compact Sort By
                        used on projects/professionals — same 1rem as the pills
                        and heading. `shrink-0` keeps the control at its
                        natural width so the heading truncates first, and
                        `max-md:min-h-[44px]` floors the trigger tap target
                        below md. */}
                        <div
                            className={cn(
                                'shrink-0 max-md:[&_button]:min-h-[44px] [&_button_svg]:size-4 [&_button>span]:text-base [&_button>span]:font-normal [&_button>span]:tracking-normal',
                                // The chevron svg has no fill attribute (defaults to
                                // black) and doesn't use currentColor, so it needs an
                                // explicit fill — text colour only flips the label.
                                isDark
                                    ? '[&_button_svg]:fill-offwhite [&_button>span]:text-offwhite'
                                    : '[&_button_svg]:fill-[#141414] [&_button>span]:text-[#323232]',
                            )}
                        >
                            {hasDisplayControl ? (
                                <DirectoryDisplayDropdown
                                    columns={displayColumns}
                                    onColumnsChange={onDisplayColumnsChange}
                                />
                            ) : showSort ? (
                                <SortDropdown
                                    sortOptions={sortOptions}
                                    updateSort={searchFilters.updateSort}
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
                {/* Applied-filter pills above the grid. Desktop-only via the
                    row's max-md:hidden: below md the Refine sheet's chips row
                    carries the applied state. */}
                <AppliedFiltersRow
                    searchFilters={searchFilters}
                    theme={isDark ? 'dark' : 'light'}
                    className="pb-6"
                />
            </div>
        </>
    );
};
